import { OktaAuth } from '@okta/okta-auth-js';
import { OktaConfig, AuthServiceConfig, Logger } from '../types';

export class OktaAuthService {
  private oktaAuth: OktaAuth;
  private config: AuthServiceConfig;
  private logger?: Logger;

  constructor(oktaConfig: OktaConfig, serviceConfig: AuthServiceConfig = {}) {
    this.oktaAuth = new OktaAuth({
      issuer: oktaConfig.issuer,
      clientId: oktaConfig.clientId,
      redirectUri: oktaConfig.redirectUri,
    });
    
    this.config = {
      scopes: ['openid', 'email'],
      ...serviceConfig,
    };
    
    this.logger = serviceConfig.logger;
  }

  /**
   * Get the Okta Auth instance
   */
  getOktaAuth(): OktaAuth {
    return this.oktaAuth;
  }

  /**
   * Silent token acquisition for session restoration
   */
  async getSilentToken(scopes: string[] = this.config.scopes || ['openid', 'email']) {
    this.logger?.info('Getting token without prompt');
    
    try {
      const tokenResponse = await this.oktaAuth.token.getWithoutPrompt({ scopes });
      return {
        success: true,
        tokens: tokenResponse.tokens,
        accessToken: tokenResponse.tokens.accessToken?.accessToken,
      };
    } catch (error) {
      this.logger?.error('Failed to get silent token');
      return {
        success: false,
        error,
        tokens: null,
        accessToken: null,
      };
    }
  }

  /**
   * Check if an Okta auth flow is currently in progress
   */
  isAuthInProgress(): boolean {
    return this.oktaAuth.transactionManager.exists();
  }

  /**
   * Redirect to login with optional original URI
   */
  signInWithRedirect(options?: { originalUri?: string }) {
    return this.oktaAuth.signInWithRedirect(options);
  }

  /**
   * Get user info using different methods based on token source
   */
  async getUserInfo(accessToken?: any, idToken?: any) {
    if (accessToken && idToken) {
      // Use tokens directly (for silent token flow)
      return await this.oktaAuth.token.getUserInfo(accessToken, idToken);
    } else {
      // Use standard method (for normal auth flow)
      return await this.oktaAuth.getUser();
    }
  }

  /**
   * Create restore original URI function
   */
  createRestoreOriginalUri(navigate: (path: string, options?: any) => void, toRelativeUrl: (url: string, origin: string) => string) {
    return async (_oktaAuth: OktaAuth, originalUri: string) => {
      this.logger?.AI?.info(`restoreOriginalUri ${originalUri}`);
      navigate(toRelativeUrl(originalUri || '/', window.location.origin), {
        replace: true,
      });
    };
  }
}
